import { ActionIcon, Affix, Transition } from '@mantine/core';
import { IconArrowDown } from '@tabler/icons-react';
import * as React from 'react';
import { useEffect, useRef, useState } from 'react';

import { useMedia } from '@mygpt/app/utils';

function useScrollToBottom() {
  // for auto-scroll
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const [autoScroll, setAutoScroll] = useState(true);

  function scrollDomToBottom() {
    const dom = scrollRef.current;
    if (dom) {
      requestAnimationFrame(() => {
        setAutoScroll(true);
        dom.scrollTo(0, dom.scrollHeight);
      });
    }
  }

  // auto scroll
  useEffect(() => {
    if (autoScroll) {
      scrollDomToBottom();
    }
  });

  return {
    scrollRef,
    autoScroll,
    setAutoScroll,
    scrollDomToBottom,
  };
}

const scrollableDivStyle = {
  maxHeight: 'inherit',
  height: 'inherit',
  overflowY: 'auto' as const,
};

export type ChatScrollProps = {
  className?: string;
  onScroll?: (isAtBottom: boolean) => void;
  children?: React.ReactNode;
  closeAffixArrow?: boolean;
  affixPosition?: {
    bottom?: string | number;
    left?: string | number;
  };
  style?: React.CSSProperties;
  containerScroll?: (e: HTMLElement) => void;
};

const ChatScroll: React.FC<ChatScrollProps> = ({
  affixPosition,
  children,
  className,
  closeAffixArrow,
  style,
  containerScroll,
}) => {
  const { autoScroll, scrollDomToBottom, scrollRef, setAutoScroll } = useScrollToBottom();
  const bottomRef = useRef<HTMLDivElement>(null);

  const { isMobile } = useMedia();
  const joinedClassName = className ? ' ' + className : '';

  const onChatBodyScroll = (e: HTMLElement) => {
    const bottomHeight = e.scrollTop + e.clientHeight;
    const isHitBottom = bottomHeight >= e.scrollHeight - (isMobile ? 4 : 10);
    setAutoScroll(isHitBottom);
  };

  // 完善图片滚动，监听图片加载完后自动往下滚动
  useEffect(() => {
    const container = scrollRef.current;

    const handleImageLoad = (event: Event) => {
      if ((event.target as HTMLElement).tagName === 'IMG' && autoScroll) {
        scrollDomToBottom();
      }
    };

    if (container) {
      // 为容器添加事件监听器，监听所有冒泡的 'load' 事件
      container.addEventListener('load', handleImageLoad, true); // true 表示在捕获阶段监听
    }

    return () => {
      if (container) {
        container.removeEventListener('load', handleImageLoad, true);
      }
    };
  }, [autoScroll, scrollDomToBottom, scrollRef]);

  // 清除会话的时候强制滚地按钮消失
  useEffect(() => {
    const container = scrollRef.current;
    const resizeObserver = new ResizeObserver(() => {
      if (container) {
        const scrollable = container.scrollHeight > container.clientHeight;
        if (!scrollable) setAutoScroll(true);
      }
    });

    if (container) {
      resizeObserver.observe(container);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <div
      className={joinedClassName}
      style={style || scrollableDivStyle}
      ref={scrollRef}
      onScroll={(e) => {
        onChatBodyScroll(e.currentTarget);
        containerScroll?.(e.currentTarget);
      }}
    >
      {children}
      {!closeAffixArrow && (
        <Affix position={{ bottom: affixPosition?.bottom || 100, left: affixPosition?.left || '50%' }}>
          <Transition transition="slide-up" mounted={!autoScroll}>
            {(transitionStyles) => (
              <ActionIcon
                style={{ ...transitionStyles, backgroundColor: 'rgba(0,0,0,.1)' }}
                radius="xl"
                variant="light"
                onClick={() => {
                  bottomRef.current &&
                    bottomRef.current.scrollIntoView({
                      behavior: 'smooth',
                      block: 'center',
                    });
                }}
              >
                <IconArrowDown color="rgba(0,0,0,.5)" size={16} />
              </ActionIcon>
            )}
          </Transition>
        </Affix>
      )}
      <div ref={bottomRef} />
    </div>
  );
};

export default ChatScroll;
